import { ECONOMY_DISCOUNT } from '../../engine/bench/cost';
/**
 * Economy (Message Batches) client (SECURITY §4, SD-1). It talks to the relay routes on our own origin
 * (`/api/anthropic/batches*`), because Anthropic rejects browser CORS for batches. The customer's key travels in `x-api-key`.
 * Results can take up to 24 hours: the caller (the resumable job queue, W5b) keeps the batch ids and collects later.
 */
import type { CompletionRequest, CompletionResponse } from '../../engine/bench/types';
import { isAllowedModel } from '../../shared/models';
import { ProviderError, type ProviderErrorInfo, errorFrom, scrub } from './errors';
import { API_VERSION, backoffMs, retryAfterMs } from './provider-anthropic';
import { buildMessageParams } from './request';

/** Batch prices are half the standard prices; the number lives in `cost.ts` (one constant). */
export const BATCH_DISCOUNT = 1 - ECONOMY_DISCOUNT;
/** Headroom under the relay's 32 MiB body cap (the JSON envelope, whitespace). */
export const MAX_BATCH_BYTES = 32 * 1024 * 1024 - 64 * 1024;
export const MAX_BATCH_REQUESTS = 100_000;

/** Longest `custom_id` we make; the API takes 1 to 64 characters of `[A-Za-z0-9_-]`. */
export const MAX_CUSTOM_ID = 40;
const CUSTOM_ID = /^[A-Za-z0-9_-]{1,40}$/;

export interface BatchKey {
	/** Paragraphs [a, b) of the chunk, its boundary version and the round. */
	a: number;
	b: number;
	v: number;
	round: number;
	/** The piece of an oversize paragraph (chunks of one paragraph share `a` and `b`). */
	piece?: number;
	/** The judge request of that round's edited dialogue (spec §11 item 2). */
	judge?: boolean;
	/** `control` and `rework` requests carry a letter so a collected result tells them apart. */
	purpose?: 'rewrite' | 'control' | 'rework';
}

const PURPOSE_TAG = { rewrite: '', control: 'k', rework: 'w' } as const;

/** `c{a}-{b}[p{piece}]v{v}r{round}`, plus `j` for the judge and `k` or `w` for a control or a rework (spec §9.4). */
export function batchCustomId(k: BatchKey): string {
	for (const n of [k.a, k.b, k.v, k.round, ...(k.piece === undefined ? [] : [k.piece])]) {
		if (!Number.isSafeInteger(n) || n < 0)
			throw new RangeError('custom_id parts are non-negative integers');
	}
	const id = `c${k.a}-${k.b}${k.piece === undefined ? '' : `p${k.piece}`}v${k.v}r${k.round}${k.judge ? 'j' : ''}${PURPOSE_TAG[k.purpose ?? 'rewrite']}`;
	if (!CUSTOM_ID.test(id)) throw new RangeError(`custom_id ${id} is longer than ${MAX_CUSTOM_ID}`);
	return id;
}

/** The inverse of `batchCustomId`; null for an id this app did not make. */
export function parseBatchCustomId(id: string): BatchKey | null {
	const m = /^c(\d+)-(\d+)(?:p(\d+))?v(\d+)r(\d+)(j?)([kw]?)$/.exec(id);
	if (!m) return null;
	return {
		a: Number(m[1]),
		b: Number(m[2]),
		...(m[3] !== undefined ? { piece: Number(m[3]) } : {}),
		v: Number(m[4]),
		round: Number(m[5]),
		...(m[6] ? { judge: true } : {}),
		purpose: m[7] === 'k' ? 'control' : m[7] === 'w' ? 'rework' : 'rewrite',
	};
}

export interface BatchRequest {
	customId: string;
	request: CompletionRequest;
}

export interface BatchStatus {
	id: string;
	processingStatus: 'in_progress' | 'canceling' | 'ended' | string;
	counts: {
		processing: number;
		succeeded: number;
		errored: number;
		canceled: number;
		expired: number;
	};
	hasResults: boolean;
}

export interface BatchResult {
	customId: string;
	status: 'succeeded' | 'errored' | 'canceled' | 'expired';
	response?: CompletionResponse;
	error?: ProviderErrorInfo;
}

export interface BatchClientOptions {
	apiKey: string;
	fetch?: typeof fetch;
	/** Relay base path on our origin. */
	base?: string;
	signal?: AbortSignal;
	maxRetries?: number;
	sleep?: (ms: number) => Promise<void>;
	random?: () => number;
}

const enc = new TextEncoder();

/** Standard's params without `stream` (spec §9.4): the same builder. */
function toParams(r: CompletionRequest) {
	return buildMessageParams(r, r.maxTokens);
}

function toEntry(b: BatchRequest) {
	return { custom_id: b.customId, params: toParams(b.request) };
}

/** Splits requests into batches that fit the relay's body cap and Anthropic's request-count cap, preserving order. */
export function splitBatches(
	requests: readonly BatchRequest[],
	opts: { maxBytes?: number; maxRequests?: number } = {}
): BatchRequest[][] {
	const maxBytes = opts.maxBytes ?? MAX_BATCH_BYTES;
	const maxRequests = opts.maxRequests ?? MAX_BATCH_REQUESTS;
	const out: BatchRequest[][] = [];
	let cur: BatchRequest[] = [];
	let size = 16; // {"requests":[]}
	for (const r of requests) {
		const bytes = enc.encode(JSON.stringify(toEntry(r))).length + 1;
		if (bytes + 16 > maxBytes) {
			throw new ProviderError({
				status: 413,
				type: 'request_too_large',
				message: `request ${r.customId} is larger than a batch may be`,
			});
		}
		if (cur.length > 0 && (size + bytes > maxBytes || cur.length >= maxRequests)) {
			out.push(cur);
			cur = [];
			size = 16;
		}
		cur.push(r);
		size += bytes;
	}
	if (cur.length > 0) out.push(cur);
	return out;
}

/** A schema request's text is JSON; a batch result does not carry the request, so the shape of the text decides. */
function parsedFromText(text: string): unknown {
	const t = text.trim();
	if (t[0] !== '{' && t[0] !== '[') return undefined;
	try {
		return JSON.parse(t);
	} catch {
		return undefined;
	}
}

function toCompletion(message: {
	content?: { type?: string; text?: string }[];
	stop_reason?: string;
	usage?: Record<string, number>;
}): CompletionResponse {
	const text = (message.content ?? [])
		.filter((b) => b.type === 'text')
		.map((b) => b.text ?? '')
		.join('');
	const u = message.usage ?? {};
	return {
		text,
		...(parsedFromText(text) !== undefined ? { parsed: parsedFromText(text) } : {}),
		stopReason: message.stop_reason ?? 'end_turn',
		usage: {
			inputTokens: u.input_tokens ?? 0,
			outputTokens: u.output_tokens ?? 0,
			cacheReadTokens: u.cache_read_input_tokens ?? 0,
			cacheWriteTokens: u.cache_creation_input_tokens ?? 0,
		},
	};
}

/** Parses the results JSONL (one result per line). */
export function parseResults(jsonl: string, secrets: readonly string[] = []): BatchResult[] {
	const out: BatchResult[] = [];
	for (const line of jsonl.split('\n')) {
		if (line.trim() === '') continue;
		let row: { custom_id?: string; result?: { type?: string; message?: never; error?: unknown } };
		try {
			row = JSON.parse(line);
		} catch {
			continue;
		}
		const customId = row.custom_id ?? '';
		const type = row.result?.type;
		if (type === 'succeeded') {
			out.push({
				customId,
				status: 'succeeded',
				response: toCompletion((row.result?.message ?? {}) as Parameters<typeof toCompletion>[0]),
			});
		} else if (type === 'errored') {
			const e = errorFrom(0, row.result?.error, secrets);
			out.push({
				customId,
				status: 'errored',
				error: { status: e.status, type: e.type, message: e.message },
			});
		} else if (type === 'canceled' || type === 'expired') {
			out.push({ customId, status: type });
		}
	}
	return out;
}

export function createBatchClient(options: BatchClientOptions) {
	const doFetch = options.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
	const base = options.base ?? '/api/anthropic/batches';
	const secrets = [options.apiKey];
	const maxRetries = options.maxRetries ?? 3;
	const sleep = options.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
	const random = options.random ?? Math.random;

	async function call(path: string, method: 'GET' | 'POST', body?: string): Promise<Response> {
		for (let n = 0; ; n++) {
			let failure: ProviderError;
			let retryAfter: number | null = null;
			try {
				const res = await doFetch(`${base}${path}`, {
					method,
					signal: options.signal,
					headers: {
						'content-type': 'application/json',
						'x-api-key': options.apiKey,
						'anthropic-version': API_VERSION,
						'x-provenance-client': '1',
					},
					body,
				});
				if (res.ok) return res;
				retryAfter = retryAfterMs(res.headers.get('retry-after'), Date.now());
				let parsed: unknown = null;
				try {
					parsed = await res.json();
				} catch {
					// no JSON body
				}
				// The relay's own errors are `{error: 'code'}`; Anthropic's are `{error: {type, message}}`.
				const relayCode = (parsed as { error?: unknown } | null)?.error;
				failure =
					typeof relayCode === 'string'
						? new ProviderError({
								status: res.status,
								type: scrub(relayCode, secrets).slice(0, 60),
								message: `relay refused: ${scrub(relayCode, secrets)}`,
							})
						: errorFrom(res.status, parsed, secrets);
			} catch (err) {
				failure =
					err instanceof ProviderError
						? err
						: new ProviderError({
								status: 0,
								type: options.signal?.aborted ? 'cancelled' : 'network_error',
								message: options.signal?.aborted ? 'cancelled' : 'network error',
							});
			}
			const retryable =
				failure.type !== 'cancelled' && [0, 429, 500, 502, 503, 504, 529].includes(failure.status);
			if (!retryable || n >= maxRetries) throw failure;
			await sleep(backoffMs(n, retryAfter, { base: 1000, max: 60_000, random }));
		}
	}

	const client = {
		/** Submits one batch. Use `splitBatches` first for a large job. */
		async create(requests: readonly BatchRequest[]): Promise<{ id: string }> {
			for (const r of requests) {
				if (!isAllowedModel(r.request.model)) {
					throw new ProviderError({
						status: 400,
						type: 'invalid_request_error',
						message: 'model not allowed',
					});
				}
			}
			const body = JSON.stringify({ requests: requests.map(toEntry) });
			if (
				enc.encode(body).length > MAX_BATCH_BYTES + 64 * 1024 ||
				requests.length > MAX_BATCH_REQUESTS
			) {
				throw new ProviderError({
					status: 413,
					type: 'request_too_large',
					message: 'the batch is too large; split it first',
				});
			}
			const res = await call('', 'POST', body);
			const id = ((await res.json()) as { id?: unknown }).id;
			if (typeof id !== 'string')
				throw new ProviderError({
					status: 502,
					type: 'api_error',
					message: 'the batch reply had no id',
				});
			return { id };
		},

		async get(id: string): Promise<BatchStatus> {
			const res = await call(`/${id}`, 'GET');
			const b = (await res.json()) as {
				id: string;
				processing_status: string;
				request_counts?: Record<string, number>;
				results_url?: string | null;
			};
			const c = b.request_counts ?? {};
			return {
				id: b.id,
				processingStatus: b.processing_status,
				counts: {
					processing: c.processing ?? 0,
					succeeded: c.succeeded ?? 0,
					errored: c.errored ?? 0,
					canceled: c.canceled ?? 0,
					expired: c.expired ?? 0,
				},
				hasResults: typeof b.results_url === 'string',
			};
		},

		async results(id: string): Promise<BatchResult[]> {
			const res = await call(`/${id}/results`, 'GET');
			return parseResults(await res.text(), secrets);
		},

		async cancel(id: string): Promise<void> {
			await call(`/${id}/cancel`, 'POST', '{}');
		},

		/**
		 * Submits the requests of one round as one or more batches (split to fit the relay's body cap) and returns the batch
		 * ids, in order, for the job record. Ids must be unique and well formed (spec §9.4).
		 */
		async submit(requests: readonly BatchRequest[]): Promise<string[]> {
			const seen = new Set<string>();
			for (const r of requests) {
				if (!CUSTOM_ID.test(r.customId) || seen.has(r.customId)) {
					throw new ProviderError({
						status: 400,
						type: 'invalid_request_error',
						message: 'custom_id is malformed or repeated',
					});
				}
				seen.add(r.customId);
			}
			const ids: string[] = [];
			for (const part of splitBatches(requests)) ids.push((await client.create(part)).id);
			return ids;
		},

		/**
		 * Reads the batches: `done` is true when every one has ended (then `results` holds all their answers); otherwise the
		 * caller polls again later (up to 24 hours), so a reload resumes from the ids alone.
		 */
		async collect(ids: readonly string[]): Promise<{
			done: boolean;
			status: BatchStatus[];
			results: BatchResult[];
		}> {
			const status: BatchStatus[] = [];
			for (const id of ids) status.push(await client.get(id));
			const done = status.every((s) => s.processingStatus === 'ended');
			const results: BatchResult[] = [];
			if (done) for (const id of ids) results.push(...(await client.results(id)));
			return { done, status, results };
		},
	};
	return client;
}
